import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, Edit, Trash2, BookOpen, Loader2 } from 'lucide-react';
import CategoryCatalogDialog from '../../components/admin/CategoryCatalogDialog';
import { getErrorMessage } from '../../utils/errors';

import { API_URL } from '../../utils/api';
const TOP_LEVEL_VALUE = '__top_level__';

export default function AdminCategories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [parentId, setParentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', image_url: '', parent_id: null });
  const [saving, setSaving] = useState(false);
  const [catalogCategory, setCatalogCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load categories'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...formData, parent_id: parentId ? parentId : null };
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      if (editingCategory) {
        await axios.put(`${API_URL}/admin/categories/${editingCategory.id}`, payload, config);
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/admin/categories`, payload, config);
        toast.success('Category created');
      }
      fetchCategories();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save category'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || '',
      parent_id: category.parent_id || null
    });
    setParentId(category.parent_id || '');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await axios.delete(`${API_URL}/admin/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete category'));
    }
  };

  // Keep the table in sync after files are uploaded/removed in the catalog dialog.
  const handleCatalogsChanged = (categoryId, catalogFiles) => {
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, catalog_files: catalogFiles } : c)));
    setCatalogCategory((c) => (c && c.id === categoryId ? { ...c, catalog_files: catalogFiles } : c));
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', image_url: '', parent_id: null });
    setParentId('');
  };

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      
      <div className="ml-64 p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold text-ink mb-2">Categories</h1>
              <p className="text-ink/60">Manage invitation categories and their PDF/JPG catalogs</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-ink text-paper hover:bg-ink/90" data-testid="add-category-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Parent Category (optional)</Label>
                    <Select
                      value={parentId || TOP_LEVEL_VALUE}
                      onValueChange={(v) => setParentId(v === TOP_LEVEL_VALUE ? '' : v)}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Top-level category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={TOP_LEVEL_VALUE}>(Top-level)</SelectItem>
                        {categories
                          .filter((c) => !c.parent_id)
                          .map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-2"
                      data-testid="category-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-2"
                      data-testid="category-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="image_url">Image URL</Label>
                    <Input
                      id="image_url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="mt-2"
                      data-testid="category-image-input"
                    />
                  </div>
                  <Button type="submit" disabled={saving} className="w-full bg-ink text-paper hover:bg-ink/90" data-testid="save-category-btn">
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingCategory ? 'Update' : 'Create'} Category
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Catalogs</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        {category.image_url && (
                          <img
                            src={category.image_url}
                            alt={category.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{category.name}</TableCell>
                      <TableCell className="text-ink/60">{category.description}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCatalogCategory(category)}
                          data-testid={`catalogs-category-${category.id}`}
                        >
                          <BookOpen className="h-4 w-4 mr-2" />
                          {(category.catalog_files || []).length > 0
                            ? `${category.catalog_files.length} file${category.catalog_files.length === 1 ? '' : 's'}`
                            : 'Add catalog'}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(category)}
                          aria-label={`Edit ${category.name}`}
                          data-testid={`edit-category-${category.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => handleDelete(category.id)}
                          aria-label={`Delete ${category.name}`}
                          data-testid={`delete-category-${category.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-12 text-center text-ink/60">
                        No categories yet. Click “Add Category” to create your first one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </motion.div>
      </div>

      <CategoryCatalogDialog
        category={catalogCategory}
        open={!!catalogCategory}
        onOpenChange={(open) => { if (!open) setCatalogCategory(null); }}
        token={token}
        onChanged={handleCatalogsChanged}
      />
    </div>
  );
}
